/** Notebook tree + pages: Tree, TreeRow, PageView, Trash, EmptyTrashDialog, Welcome, NotFound. */
export const notebook = {
  'notebook.untitled': 'Untitled',
  'notebook.welcome.empty': 'Nothing here yet.',
  'notebook.welcome.body':
    'Create your first page to start your notebook. Pages, the tree and the editor arrive in M2.',
  'notebook.notFound.title': "This page doesn't exist.",
  'notebook.notFound.body': 'It may have been deleted permanently.',
  'notebook.notFound.back': 'Back to notebook',

  /* ── Tree ─────────────────────────────────────────────────────────────── */
  'notebook.pages.title': 'Pages',
  'notebook.pages.new': 'New page',
  'notebook.pages.loading': 'Loading pages…',
  'notebook.pages.loadError': "Couldn't load the tree.",
  'notebook.pages.emptyBody': 'Create your first page to start your notebook.',
  'notebook.collapse': 'Collapse',
  'notebook.expand': 'Expand',
  'notebook.pageTitleLabel': 'Page title',
  'notebook.addSubpage': 'Add sub-page',
  'notebook.notSyncedLabel': 'Not synced yet',
  'notebook.notSyncedTitle': 'Saved on this device — will sync when the server is reachable',
  'notebook.actionsFor': 'Actions for {title}',
  'notebook.removeFromFavorites': 'Remove {title} from favorites',
  'notebook.addToFavorites': 'Add {title} to favorites',
  'notebook.addSubpageFor': 'Add a sub-page to {title}',

  'notebook.archiveConfirm.title': 'Archive “{title}”?',
  'notebook.archiveConfirm.body.one':
    'Its {count} sub-page will be archived too. You can restore them from the trash.',
  'notebook.archiveConfirm.body.other':
    'Its {count} sub-pages will be archived too. You can restore them from the trash.',
  'notebook.archiveConfirm.confirm': 'Archive',
  'notebook.archivedToast': 'Archived “{title}”.',

  'notebook.toast.createError': "Couldn't create the page.",

  /* ── Trash (sidebar peek + /trash room) ──────────────────────────────────── */
  'notebook.trash.title': 'Trash',
  'notebook.trash.open': 'Open',
  'notebook.trash.openTitle': 'Open the trash (G then T)',
  'notebook.trash.empty': 'The trash is empty.',
  'notebook.trash.restoreAriaLabel': 'Restore {title}',
  'notebook.trash.restoreTitle': 'Restore',
  'notebook.trash.deletePermanentlyAriaLabel': 'Delete {title} permanently',
  'notebook.trash.deletePermanentlyTitle': 'Delete permanently',
  'notebook.trash.restoringToast': 'Restoring “{title}”…',
  'notebook.trash.deleteConfirmTitle': 'Delete “{title}” permanently?',
  'notebook.trash.deleteConfirmBodySimple':
    'This page and everything under it will be gone for good. This cannot be undone.',
  'notebook.trash.deletedToast': 'Deleted “{title}”.',
  'notebook.trash.pageCount.one': '{count} page',
  'notebook.trash.pageCount.other': '{count} pages',
  'notebook.trash.emptyTrashButton': 'Empty trash',
  'notebook.trash.loadErrorTitle': "Couldn't load the trash.",
  'notebook.trash.loadErrorBody': "The server didn't respond.",
  'notebook.trash.emptyStateBody':
    'The trash is empty. Archived pages land here, with everything under them.',
  'notebook.trash.subpageCount.one': '+{count} sub-page',
  'notebook.trash.subpageCount.other': '+{count} sub-pages',
  'notebook.trash.restoreButton': 'Restore',
  'notebook.trash.deleteButton': 'Delete',
  'notebook.trash.deleteConfirmBodyWithDescendants.one':
    'This page and its {count} sub-page will be gone for good, along with the images only they used. This cannot be undone.',
  'notebook.trash.deleteConfirmBodyWithDescendants.other':
    'This page and its {count} sub-pages will be gone for good, along with the images only they used. This cannot be undone.',
  'notebook.trash.deleteConfirmBodyNoDescendants':
    'This page will be gone for good, along with the images only it used. This cannot be undone.',

  /* ── Empty trash dialog ──────────────────────────────────────────────────── */
  'notebook.emptyTrash.title.one': 'Permanently delete {count} page?',
  'notebook.emptyTrash.title.other': 'Permanently delete {count} pages?',
  'notebook.emptyTrash.bodyBefore':
    'Every page in the trash, and every image only those pages used, will be deleted from the server. ',
  'notebook.emptyTrash.bodyStrong': 'This is irreversible.',
  'notebook.emptyTrash.bodyAfter': ' There is no undo and no version history.',
  // Split around the count so it can render monospace + semibold (the count is the guard).
  'notebook.emptyTrash.confirmBefore': 'Type ',
  'notebook.emptyTrash.confirmAfter': ' to confirm.',
  'notebook.emptyTrash.deletingToast.one': 'Deleting {count} page…',
  'notebook.emptyTrash.deletingToast.other': 'Deleting {count} pages…',
  'notebook.emptyTrash.error': "Couldn't empty the trash.",

  /* ── PageView ─────────────────────────────────────────────────────────────── */
  'notebook.createdToast': 'Created “{title}”.',
  'notebook.couldntOpen': "Couldn't open this page.",
  'notebook.breadcrumbLabel': 'Breadcrumb',
  'notebook.removeFromFavoritesHeader': 'Remove from favorites',
  'notebook.addToFavoritesHeader': 'Add to favorites',
  'notebook.archivedNotice': 'This page is in the trash. Restore it to edit.',
  'notebook.offlineNotice':
    'Showing your local copy — the server is unreachable. Keep typing; it will sync.',
  'notebook.switchedToServer': 'Switched to the server version.',
  'notebook.switchedToServerDescription': 'Your local version was kept as a backup.',
  'notebook.undo': 'Undo',

  /* ── Backlinks ────────────────────────────────────────────────────────────── */
  'notebook.backlinks.ariaLabel': 'Backlinks',
  'notebook.backlinks.title': 'Linked from',
  'notebook.backlinks.none': 'No pages link here yet.',
  'notebook.backlinks.unresolvedHeading': 'Unresolved links on this page ({count})',
} as const;
